/**
 * What to do when a page's code will not load.
 *
 * THE FAULT THIS EXISTS FOR, measured on both deployments rather than guessed:
 * hashed assets do NOT survive a deploy, and they do not 404 either. The static
 * host answers any unmatched path with index.html and a 200, so a request for
 * `assets/KioskPage-De0arX4X.js` from an older build comes back as
 * `text/html`, and the browser reports
 *
 *     TypeError: Failed to fetch dynamically imported module
 *
 * The same string appears when the device is simply offline and the chunk was
 * never precached. Two completely different situations, one error — and the
 * right response to each is the opposite of the right response to the other:
 *
 *   STALE (online): the running page belongs to a build that no longer exists
 *   on the server. One reload fetches the current index.html and the current
 *   hashes, and everything works again. Waiting achieves nothing.
 *
 *   OFFLINE: the chunk genuinely cannot be fetched from anywhere. A reload is
 *   the worst possible move — it would throw away a perfectly good running page
 *   and land on whatever the precache holds, in a loop. Say what has happened
 *   and leave the app alone.
 *
 * So the whole policy is: is this a chunk-load failure, and are we online?
 *
 * The reload is fired at most once per session per route. A device that is
 * online but wedged — a proxy rewriting assets, a half-broken deploy — would
 * otherwise reload for ever, which is worse than a message.
 */

/** Session-scoped so it resets on a genuine restart but survives the reload it
 *  triggers, which is exactly the window a loop would form in. */
const RELOAD_MARK = 'shell-recovery-reloaded';

/**
 * Is this the browser failing to fetch a JS module, rather than a bug in ours?
 *
 * Matched on the message because that is all the platform gives us: browsers
 * report a plain TypeError with no code and no cause, and the wording differs
 * between engines. Kept deliberately broad — a false positive costs one reload,
 * a false negative shows a person a stack trace they cannot act on.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||   // Chrome, Edge
    /error loading dynamically imported module/i.test(message) ||     // Firefox
    /Importing a module script failed/i.test(message) ||              // Safari
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message)
  );
}

/** navigator.onLine is a weak signal — it means "there is a network
 *  interface", not "the server is reachable". It is used only to choose
 *  between two recoveries, never to decide whether something worked, and for
 *  that it is the right tool. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Try to recover from a failed chunk load. Returns true if a reload was
 * started, in which case the caller should render nothing useful — the page is
 * about to go.
 */
export function recoverFromChunkError(error: unknown, key = 'global'): boolean {
  if (!isChunkLoadError(error)) return false;
  if (isOffline()) return false;              // a reload cannot help; say so instead

  const mark = `${RELOAD_MARK}:${key}`;
  try {
    if (sessionStorage.getItem(mark)) return false;   // already tried; stop
    sessionStorage.setItem(mark, String(Date.now()));
  } catch {
    // Storage disabled. Without a way to remember, one reload could become a
    // loop, so do not start one.
    return false;
  }

  window.location.reload();
  return true;
}

/** Called after a page renders successfully, so the next stale deploy gets its
 *  one reload rather than being refused because of an old mark. */
export function clearRecoveryMark(key = 'global'): void {
  try {
    sessionStorage.removeItem(`${RELOAD_MARK}:${key}`);
  } catch {
    /* nothing to clear */
  }
}
